import React from 'react'
import { Video } from 'lucide-react'
import ContentRenderer from '../../../components/ContentRenderer'
import { cardToContentItems } from '../lib/cardToContentItem'
import { genId } from '../lib/id'
import type { VideoCard } from '../types/LessonDoc'
import type { CardDef, CardEditorProps, CardPreviewProps } from './types'

// Trích id video YouTube từ URL hoặc trả lại chuỗi nếu đã là id.
function parseYouTubeId(input: string): string | null {
  if (!input) return null
  const m = input.match(/(?:youtu\.be\/|v=|embed\/|shorts\/)([\w-]{11})/)
  if (m) return m[1]
  if (/^[\w-]{11}$/.test(input.trim())) return input.trim()
  return null
}

const Editor: React.FC<CardEditorProps> = ({ card, onChange }) => {
  const c = card as VideoCard
  return (
    <div className="space-y-3">
      <div>
        <label className="text-xs font-bold text-gray-500 uppercase">Link YouTube hoặc URL video</label>
        <input
          className="w-full p-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200"
          value={c.youtubeId || c.src || ''}
          onChange={(e) => {
            const val = e.target.value
            const yt = parseYouTubeId(val)
            if (yt) onChange({ youtubeId: yt, src: undefined })
            else onChange({ src: val, youtubeId: undefined })
          }}
          placeholder="https://youtu.be/... hoặc https://.../video.mp4"
        />
      </div>
      <div>
        <label className="text-xs font-bold text-gray-500 uppercase">Chú thích (tuỳ chọn)</label>
        <input
          className="w-full p-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200"
          value={c.caption || ''}
          onChange={(e) => onChange({ caption: e.target.value })}
        />
      </div>
    </div>
  )
}

const Preview: React.FC<CardPreviewProps> = ({ card }) => {
  const c = card as VideoCard
  // YouTube: nhúng iframe trực tiếp (ContentRenderer không nhận diện được id 11 ký tự).
  if (c.youtubeId) {
    return (
      <div className="my-2">
        {c.title && <h4 className="text-base font-bold text-gray-900 mb-2">{c.title}</h4>}
        <div className="relative w-full overflow-hidden rounded-xl border border-gray-200 bg-black" style={{ paddingBottom: '56.25%' }}>
          <iframe
            className="absolute inset-0 w-full h-full"
            src={`https://www.youtube.com/embed/${c.youtubeId}`}
            title={c.title || 'YouTube video'}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>
        {c.caption && <p className="text-xs sm:text-sm text-gray-600 mt-2 italic text-center">{c.caption}</p>}
      </div>
    )
  }
  const items = cardToContentItems(card)
  return items ? <ContentRenderer content={items} /> : null
}

export const videoCardDef: CardDef = {
  type: 'video',
  label: 'Video',
  description: 'YouTube hoặc video tải lên',
  icon: Video,
  color: 'bg-rose-50 text-rose-600',
  createDefault: (): VideoCard => ({ id: genId(), type: 'video' }),
  Editor,
  Preview,
}
